import type {
  FeatureKey,
  SettingsCategory,
} from '@attendance/shared/schemas/settings';
import { RECORDING_GROUP } from './settingsCatalog.recording.js';
import { SECURITY_GROUP } from './settingsCatalog.security.js';

/**
 * The settings catalogue.
 *
 * Every editable setting in the app is described here ONCE, as data. The page
 * renders from this list; it does not hand-write a form per API category.
 *
 * Two ideas do the heavy lifting:
 *
 * 1. **Display grouping is decoupled from the API category.** An admin thinks
 *    "overtime rules", not "AttendanceSettings" — so `weekendDays` (which
 *    decides who gets the weekend premium) sits next to the OT multipliers even
 *    though it is stored under `general`, and the Ramadan window sits with the
 *    other things that change the target working day even though it is stored
 *    under `uaeCompliance`. Each field still carries the `category` + `path` it
 *    is persisted at, which is what the save layer patches.
 *
 * 2. **`pay: true` is load-bearing, not decoration.** It marks a setting whose
 *    change re-prices labour — overtime, grace, break windows, workday length,
 *    and anything that can cause a punch to be REJECTED (a rejected punch is
 *    unpaid time). Those fields render a warning, show a before → after line the
 *    moment they are edited, and are listed back to the admin in a confirm step
 *    before the save goes out.
 *
 * Copy lives in i18n (`settings.*` in en/ar common.json); this file only holds
 * keys, so nothing here needs translating twice.
 */

/** Categories the page can PATCH. `branding` saves through its own dedicated form (see the `companyIdentity` custom block), not the generic field/patch machinery below. */
export type EditableCategory = Exclude<SettingsCategory, 'branding'>;

export type ControlKind =
  | 'toggle'
  | 'number'
  | 'time'
  | 'date'
  | 'mmdd'
  | 'text'
  | 'textarea'
  | 'select'
  | 'timezone'
  | 'weekdays'
  | 'dayList'
  | 'schedules';

export interface SelectOption {
  value: string;
  /** i18n key for the option label. */
  labelKey: string;
}

export interface SettingField {
  /** Stable id — DOM ids, change tracking, and the i18n key namespace. */
  id: string;
  category: EditableCategory;
  /** Dot path inside the category value. `''` means the whole category value. */
  path: string;
  kind: ControlKind;
  /** Changing this changes what someone is paid. */
  pay?: boolean;
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
  /** i18n key for the unit shown beside a number ("hours", "minutes", "m"). */
  unitKey?: string;
  options?: readonly SelectOption[];
  /** Render the value right-to-left regardless of UI direction (Arabic letter bodies). */
  forceRtl?: boolean;
  /** Sibling paths written with the same value (OT2 keeps weekend + holiday in step). */
  alsoWrites?: readonly string[];
  /** Field id whose truthiness enables this control. */
  enabledBy?: string;
  /** Extra i18n keys whose text should also match this field in search. */
  keywordKeys?: readonly string[];
}

export interface SettingSection {
  id: string;
  fields: readonly SettingField[];
  /** Section-level pay banner (the whole section re-prices labour). */
  pay?: boolean;
  /**
   * Hidden entirely when this feature module is off — the same gate a GROUP
   * carries, at section grain. It exists because a section is sometimes the
   * whole of a module's settings (scheduled reports inside Notifications), and
   * a module that is switched off must not leave one live control behind in a
   * group that is otherwise still relevant.
   */
  feature?: FeatureKey;
}

export interface SettingGroup {
  id: string;
  /** Hidden entirely when this feature module is off. */
  feature?: FeatureKey;
  /** The whole group changes pay. */
  pay?: boolean;
  sections: readonly SettingSection[];
  /** Group renders a bespoke block alongside its sections. */
  custom?:
    | 'featurePresets'
    | 'kioskLock'
    | 'managedElsewhere'
    | 'letterTokens'
    | 'companyIdentity';
}

/**
 * Placeholders `letter.service.ts` interpolates. Kept as data, not copy — they
 * are literal tokens and must never pass through i18n interpolation, which
 * would eat them.
 */
export const LETTER_TOKENS = [
  '{{employeeName}}',
  '{{companyName}}',
  '{{legalName}}',
  '{{designation}}',
  '{{department}}',
  '{{joiningDate}}',
  '{{issueDate}}',
  '{{passport}}',
  '{{emiratesId}}',
  '{{purpose}}',
  '{{currency}}',
  '{{basicSalary}}',
  '{{allowances}}',
  '{{totalSalary}}',
] as const;

// ---------------------------------------------------------------------------
// Option sets
// ---------------------------------------------------------------------------

const AUTO_PUNCH_OUT_OPTIONS = [
  { value: 'flag', labelKey: 'settings.opt.autoOut.flag' },
  { value: 'mark_complete', labelKey: 'settings.opt.autoOut.markComplete' },
  { value: 'none', labelKey: 'settings.opt.autoOut.none' },
] as const;

const FISCAL_ALIGNMENT_OPTIONS = [
  { value: 'calendar', labelKey: 'settings.opt.fiscal.calendar' },
  { value: 'joining_date', labelKey: 'settings.opt.fiscal.joining' },
] as const;

const WPS_BANK_OPTIONS = [
  { value: 'GENERIC_SIF', labelKey: 'settings.opt.wps.generic' },
  { value: 'ADCB', labelKey: 'settings.opt.wps.adcb' },
  { value: 'ENBD', labelKey: 'settings.opt.wps.enbd' },
  { value: 'FAB', labelKey: 'settings.opt.wps.fab' },
  { value: 'MASHREQ', labelKey: 'settings.opt.wps.mashreq' },
  { value: 'RAKBANK', labelKey: 'settings.opt.wps.rakbank' },
] as const;

const DATE_FORMAT_OPTIONS = [
  { value: 'DD-MM-YYYY', labelKey: 'settings.opt.dateFormat.dmy' },
  { value: 'MM-DD-YYYY', labelKey: 'settings.opt.dateFormat.mdy' },
  { value: 'YYYY-MM-DD', labelKey: 'settings.opt.dateFormat.ymd' },
] as const;

const TIME_FORMAT_OPTIONS = [
  { value: '24h', labelKey: 'settings.opt.timeFormat.h24' },
  { value: '12h', labelKey: 'settings.opt.timeFormat.h12' },
] as const;

const TERMINOLOGY_PROFILE_OPTIONS = [
  { value: 'hr', labelKey: 'settings.opt.profile.hr' },
  { value: 'construction', labelKey: 'settings.opt.profile.construction' },
] as const;

/**
 * The feature flags, in the order they read best. Every one is a `toggle` on
 * the `features` category whose path is the flag name, so they need no bespoke
 * handling — only the "what disappears" copy, which lives in i18n under
 * `settings.f.feature.<key>.hint`.
 */
const FEATURE_ORDER: readonly FeatureKey[] = [
  'workers',
  // Directly under the site-workforce pillar it narrows: camps are where those
  // workers sleep, so a tenant reads the two together.
  'camps',
  'selfService',
  'corrections',
  'shifts',
  'dailyRoster',
  'team',
  'orgStructure',
  'lifecycle',
  'leave',
  'announcements',
  'letters',
  'payroll',
  'gratuity',
  'reports',
  'documents',
  'faceRecognition',
  // Directly under face enrolment: both are "how attendance gets physically
  // captured", just one on a phone/tablet and one on standalone hardware.
  'hardwareTerminals',
];

function featureField(key: FeatureKey): SettingField {
  return { id: `feature.${key}`, category: 'features', path: key, kind: 'toggle' };
}

const TERM_KEYS = [
  'employees',
  'employee',
  'workers',
  'worker',
  'worksites',
  'worksite',
  'team',
] as const;

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

export const SETTING_GROUPS: readonly SettingGroup[] = [
  // -------------------------------------------------------------------------
  // 1. Pay rules. Everything here changes what a worker is paid for the same
  //    hours on site, which is why it is first and why it is fenced off.
  // -------------------------------------------------------------------------
  {
    id: 'pay',
    pay: true,
    sections: [
      {
        id: 'pay.day',
        pay: true,
        fields: [
          {
            // The company's working START. It had no control on this page at
            // all — it lived only in the stored JSON, the rollup and one util —
            // while being the value every no-shift worker's lateness is now
            // measured from. A rule nobody can see is a rule nobody can fix.
            //
            // Not marked `pay`: it moves no worked, overtime or break figure.
            // It decides whether a day is stamped LATE, and payslips already
            // count PRESENT and LATE alike.
            id: 'workday.start',
            category: 'defaultWorkday',
            path: 'shiftStartTime',
            kind: 'time',
          },
          {
            // The company's working END, added alongside the start it had been
            // missing from. `shiftEndTime` has existed in the settings schema
            // and the employee override since it was added, but only the START
            // had a control here — so a tenant could be told "the working day
            // runs 07:00 to 16:00" and could only ever edit the 07:00. An
            // asymmetric pair is worse than an absent one: the visible half
            // reads as the whole rule.
            //
            // Not marked `pay`, for the same reason the start is not: hours
            // come from actual presence and overtime from the standard day plus
            // the tail grace. Neither end of this pair moves a figure.
            id: 'workday.end',
            category: 'defaultWorkday',
            path: 'shiftEndTime',
            kind: 'time',
          },
          {
            id: 'workday.hours',
            category: 'defaultWorkday',
            path: 'standardDailyHours',
            kind: 'number',
            pay: true,
            min: 1,
            max: 24,
            step: 0.5,
            unitKey: 'settings.unit.hours',
          },
          {
            id: 'workday.otTail',
            category: 'defaultWorkday',
            path: 'otTailGraceMinutes',
            kind: 'number',
            pay: true,
            min: 0,
            max: 240,
            step: 5,
            unitKey: 'settings.unit.minutes',
          },
          {
            id: 'general.weekendDays',
            category: 'general',
            path: 'weekendDays',
            kind: 'weekdays',
            pay: true,
          },
          {
            id: 'general.timezone',
            category: 'general',
            path: 'timezone',
            kind: 'timezone',
            pay: true,
          },
        ],
      },
      {
        id: 'pay.overtime',
        pay: true,
        fields: [
          {
            id: 'overtime.enabled',
            category: 'overtime',
            path: 'enabled',
            kind: 'toggle',
            pay: true,
          },
          {
            id: 'overtime.ot1',
            category: 'overtime',
            path: 'weekdayMultiplier',
            kind: 'number',
            pay: true,
            min: 1,
            max: 5,
            step: 0.05,
            unitKey: 'settings.unit.times',
            enabledBy: 'overtime.enabled',
          },
          {
            // One control, two stored keys: the schema keeps weekend and
            // holiday multipliers apart for payslip auditability, but the
            // business runs a single OT2 rate. Writing both keeps them from
            // silently drifting apart.
            id: 'overtime.ot2',
            category: 'overtime',
            path: 'weekendMultiplier',
            alsoWrites: ['holidayMultiplier'],
            kind: 'number',
            pay: true,
            min: 1,
            max: 5,
            step: 0.05,
            unitKey: 'settings.unit.times',
            enabledBy: 'overtime.enabled',
          },
          {
            id: 'overtime.threshold',
            category: 'overtime',
            path: 'thresholdMinutesPerDay',
            kind: 'number',
            pay: true,
            min: 0,
            max: 720,
            step: 5,
            unitKey: 'settings.unit.minutes',
            enabledBy: 'overtime.enabled',
          },
          {
            id: 'overtime.maxDaily',
            category: 'overtime',
            path: 'maxDailyMinutes',
            kind: 'number',
            pay: true,
            min: 0,
            max: 720,
            step: 15,
            unitKey: 'settings.unit.minutes',
            enabledBy: 'overtime.enabled',
          },
        ],
      },
      {
        id: 'pay.breaks',
        pay: true,
        fields: [
          {
            id: 'workday.breakStart',
            category: 'defaultWorkday',
            path: 'normalBreakWindow.start',
            kind: 'time',
            pay: true,
          },
          {
            id: 'workday.breakEnd',
            category: 'defaultWorkday',
            path: 'normalBreakWindow.end',
            kind: 'time',
            pay: true,
          },
          {
            id: 'workday.summerEnabled',
            category: 'defaultWorkday',
            path: 'summerBreakEnabled',
            kind: 'toggle',
            pay: true,
          },
          {
            id: 'workday.summerStart',
            category: 'defaultWorkday',
            path: 'summerBreakWindow.start',
            kind: 'time',
            pay: true,
            enabledBy: 'workday.summerEnabled',
          },
          {
            id: 'workday.summerEnd',
            category: 'defaultWorkday',
            path: 'summerBreakWindow.end',
            kind: 'time',
            pay: true,
            enabledBy: 'workday.summerEnabled',
          },
          {
            id: 'workday.summerFrom',
            category: 'defaultWorkday',
            path: 'summerBreakStartDate',
            kind: 'mmdd',
            pay: true,
            enabledBy: 'workday.summerEnabled',
          },
          {
            id: 'workday.summerTo',
            category: 'defaultWorkday',
            path: 'summerBreakEndDate',
            kind: 'mmdd',
            pay: true,
            enabledBy: 'workday.summerEnabled',
          },
        ],
      },
      {
        id: 'pay.ramadan',
        pay: true,
        fields: [
          {
            id: 'uae.ramadanEnabled',
            category: 'uaeCompliance',
            path: 'ramadanModeEnabled',
            kind: 'toggle',
            pay: true,
          },
          {
            id: 'uae.ramadanHours',
            category: 'uaeCompliance',
            path: 'ramadanShiftReductionHours',
            kind: 'number',
            pay: true,
            min: 0,
            max: 8,
            step: 0.5,
            unitKey: 'settings.unit.hours',
            enabledBy: 'uae.ramadanEnabled',
          },
          {
            id: 'uae.ramadanBreak',
            category: 'uaeCompliance',
            path: 'ramadanBreakMinutes',
            kind: 'number',
            pay: true,
            min: 0,
            max: 240,
            step: 5,
            unitKey: 'settings.unit.minutes',
            enabledBy: 'uae.ramadanEnabled',
          },
          {
            id: 'uae.ramadanFrom',
            category: 'uaeCompliance',
            path: 'ramadanStartDate',
            kind: 'date',
            pay: true,
            enabledBy: 'uae.ramadanEnabled',
          },
          {
            id: 'uae.ramadanTo',
            category: 'uaeCompliance',
            path: 'ramadanEndDate',
            kind: 'date',
            pay: true,
            enabledBy: 'uae.ramadanEnabled',
          },
        ],
      },
      {
        id: 'pay.classification',
        pay: true,
        fields: [
          {
            id: 'attendance.grace',
            category: 'attendance',
            path: 'graceDefaultMinutes',
            kind: 'number',
            pay: true,
            min: 0,
            max: 120,
            step: 5,
            unitKey: 'settings.unit.minutes',
          },
          {
            id: 'attendance.halfDay',
            category: 'attendance',
            path: 'halfDayThresholdHours',
            kind: 'number',
            pay: true,
            min: 0,
            max: 24,
            step: 0.25,
            unitKey: 'settings.unit.hours',
          },
          {
            id: 'attendance.fullDay',
            category: 'attendance',
            path: 'fullDayThresholdHours',
            kind: 'number',
            pay: true,
            min: 0,
            max: 24,
            step: 0.25,
            unitKey: 'settings.unit.hours',
          },
          {
            id: 'attendance.autoOutEnabled',
            category: 'attendance',
            path: 'autoPunchOut.enabled',
            kind: 'toggle',
            pay: true,
          },
          {
            id: 'attendance.autoOutAt',
            category: 'attendance',
            path: 'autoPunchOut.atTime',
            kind: 'time',
            pay: true,
            enabledBy: 'attendance.autoOutEnabled',
          },
          {
            id: 'attendance.autoOutAction',
            category: 'attendance',
            path: 'autoPunchOut.action',
            kind: 'select',
            pay: true,
            options: AUTO_PUNCH_OUT_OPTIONS,
            enabledBy: 'attendance.autoOutEnabled',
          },
          // The nudge that runs BEFORE the auto-close above, so the worker ends
          // their own day at the real time instead of the record being repaired
          // for them. Sits beside it because reading one without the other
          // makes neither make sense.
          {
            id: 'attendance.missedOutReminder',
            category: 'attendance',
            path: 'missedCheckoutReminder.enabled',
            kind: 'toggle',
            pay: true,
          },
          {
            id: 'attendance.missedOutAfter',
            category: 'attendance',
            path: 'missedCheckoutReminder.afterMinutes',
            kind: 'number',
            pay: true,
            min: 60,
            max: 1440,
            step: 30,
            unitKey: 'settings.unit.minutes',
            enabledBy: 'attendance.missedOutReminder',
          },
        ],
      },
      // Labour-law limits and the payroll file — folded in from their own
      // former tab. Both are payroll-adjacent (a threshold the compliance
      // report flags against, the bank layout the wage file is written in),
      // and a two-section tab of its own was a signpost to a cupboard.
      {
        id: 'compliance.limits',
        fields: [
          {
            id: 'uae.maxConsecutive',
            category: 'uaeCompliance',
            path: 'maxConsecutiveWorkDays',
            kind: 'number',
            min: 1,
            max: 14,
            step: 1,
            unitKey: 'settings.unit.days',
          },
          {
            id: 'uae.maxWeekly',
            category: 'uaeCompliance',
            path: 'maxWeeklyHours',
            kind: 'number',
            min: 20,
            max: 80,
            step: 1,
            unitKey: 'settings.unit.hours',
          },
        ],
      },
      {
        id: 'compliance.wps',
        fields: [
          {
            id: 'uae.wpsBank',
            category: 'uaeCompliance',
            path: 'wpsBankCode',
            kind: 'select',
            options: WPS_BANK_OPTIONS,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 2. Recording attendance — extracted to settingsCatalog.recording.ts.
  // -------------------------------------------------------------------------
  RECORDING_GROUP,

  // -------------------------------------------------------------------------
  // 3. Time off.
  // -------------------------------------------------------------------------
  {
    id: 'timeoff',
    feature: 'leave',
    sections: [
      {
        id: 'timeoff.balances',
        fields: [
          {
            id: 'leave.fiscal',
            category: 'leave',
            path: 'fiscalYearAlignment',
            kind: 'select',
            options: FISCAL_ALIGNMENT_OPTIONS,
          },
          {
            id: 'leave.notice',
            category: 'leave',
            path: 'minNoticeDaysDefault',
            kind: 'number',
            min: 0,
            max: 365,
            step: 1,
            unitKey: 'settings.unit.days',
          },
          {
            id: 'leave.negative',
            category: 'leave',
            path: 'allowNegativeBalance',
            kind: 'toggle',
            pay: true,
          },
          {
            id: 'leave.weekends',
            category: 'leave',
            path: 'weekendIncludedInLeave',
            kind: 'toggle',
            pay: true,
          },
          {
            id: 'leave.holidays',
            category: 'leave',
            path: 'holidaysIncludedInLeave',
            kind: 'toggle',
            pay: true,
          },
        ],
      },
      {
        id: 'timeoff.routing',
        fields: [
          {
            id: 'leave.notifyManager',
            category: 'leave',
            path: 'notifyManagerOnRequest',
            kind: 'toggle',
          },
          {
            id: 'leave.notifyHr',
            category: 'leave',
            path: 'notifyHrOnRequest',
            kind: 'toggle',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 4. Notifications.
  // -------------------------------------------------------------------------
  {
    id: 'notifications',
    sections: [
      {
        id: 'notifications.delivery',
        fields: [
          {
            id: 'notif.channelEmail',
            category: 'notifications',
            path: 'channels.email',
            kind: 'toggle',
          },
          {
            id: 'notif.fromName',
            category: 'notifications',
            path: 'email.fromName',
            kind: 'text',
            maxLength: 120,
          },
          {
            id: 'notif.fromEmail',
            category: 'notifications',
            path: 'email.fromEmail',
            kind: 'text',
            maxLength: 200,
          },
          {
            id: 'notif.smtpHost',
            category: 'notifications',
            path: 'email.smtpHost',
            kind: 'text',
            maxLength: 200,
          },
          {
            id: 'notif.smtpPort',
            category: 'notifications',
            path: 'email.smtpPort',
            kind: 'number',
            min: 1,
            max: 65535,
            step: 1,
          },
          {
            id: 'notif.smtpUser',
            category: 'notifications',
            path: 'email.smtpUser',
            kind: 'text',
            maxLength: 200,
          },
          {
            id: 'notif.smtpSecure',
            category: 'notifications',
            path: 'email.smtpSecure',
            kind: 'toggle',
          },
        ],
      },
      {
        id: 'notifications.triggers',
        fields: [
          {
            id: 'notif.lateToManager',
            category: 'notifications',
            path: 'triggers.lateArrivalToManager',
            kind: 'toggle',
          },
        ],
      },
      {
        // Its own section ONLY so it can carry `feature: 'documents'`. The job
        // behind it (`document-expiry`) is gated on that flag in
        // `jobs/catalog.ts` SKIP_WHEN_DISABLED, so with the module off the
        // control is inert — and an inert control sitting beside a live one,
        // in the same list, with nothing to say for itself, is exactly what
        // §3.11 forbids. A field-level gate would have been smaller; the
        // catalogue only gates at section grain, and hiding a section is
        // cheaper than inventing a second gating mechanism.
        id: 'notifications.documents',
        feature: 'documents',
        fields: [
          {
            id: 'notif.docExpiryDays',
            category: 'notifications',
            path: 'triggers.documentExpiryAlertDaysBefore',
            kind: 'dayList',
          },
        ],
      },
      {
        // Scheduled reports: a LIST rather than a value, so it gets a bespoke
        // control — but it is still a catalogue FIELD, which is what gives it
        // dirty tracking, the same save path and the same search. Its path is
        // '' (the whole category value): deepMerge replaces arrays wholesale,
        // the only reading of "the list is now this" that can express a
        // removal.
        //
        // It lives with the notification triggers rather than in a group of its
        // own: a scheduled report IS an email on a timer, and a top-level
        // destination holding a single control is a signpost to a cupboard.
        id: 'schedules',
        feature: 'reports',
        fields: [
          {
            id: 'reportSchedules',
            category: 'reportSchedules',
            path: '',
            kind: 'schedules',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 5. Letters.
  // -------------------------------------------------------------------------
  {
    id: 'letters',
    feature: 'letters',
    custom: 'letterTokens',
    sections: [
      {
        id: 'letters.shared',
        fields: [
          {
            id: 'letters.nocPurpose',
            category: 'letters',
            path: 'defaultNocPurpose',
            kind: 'text',
            maxLength: 200,
          },
        ],
      },
      {
        id: 'letters.salary',
        fields: [
          {
            id: 'letters.salaryTitle',
            category: 'letters',
            path: 'titles.salaryCertificate',
            kind: 'text',
            maxLength: 120,
          },
          {
            id: 'letters.salaryBody',
            category: 'letters',
            path: 'salaryCertificate',
            kind: 'textarea',
            maxLength: 4000,
          },
          {
            id: 'letters.salaryTitleAr',
            category: 'letters',
            path: 'titles.salaryCertificateAr',
            kind: 'text',
            maxLength: 120,
            forceRtl: true,
          },
          {
            id: 'letters.salaryBodyAr',
            category: 'letters',
            path: 'salaryCertificateAr',
            kind: 'textarea',
            maxLength: 4000,
            forceRtl: true,
          },
        ],
      },
      {
        id: 'letters.noc',
        fields: [
          {
            id: 'letters.nocTitle',
            category: 'letters',
            path: 'titles.noc',
            kind: 'text',
            maxLength: 120,
          },
          {
            id: 'letters.nocBody',
            category: 'letters',
            path: 'noc',
            kind: 'textarea',
            maxLength: 4000,
          },
          {
            id: 'letters.nocTitleAr',
            category: 'letters',
            path: 'titles.nocAr',
            kind: 'text',
            maxLength: 120,
            forceRtl: true,
          },
          {
            id: 'letters.nocBodyAr',
            category: 'letters',
            path: 'nocAr',
            kind: 'textarea',
            maxLength: 4000,
            forceRtl: true,
          },
        ],
      },
      {
        id: 'letters.experience',
        fields: [
          {
            id: 'letters.expTitle',
            category: 'letters',
            path: 'titles.experience',
            kind: 'text',
            maxLength: 120,
          },
          {
            id: 'letters.expBody',
            category: 'letters',
            path: 'experience',
            kind: 'textarea',
            maxLength: 4000,
          },
          {
            id: 'letters.expTitleAr',
            category: 'letters',
            path: 'titles.experienceAr',
            kind: 'text',
            maxLength: 120,
            forceRtl: true,
          },
          {
            id: 'letters.expBodyAr',
            category: 'letters',
            path: 'experienceAr',
            kind: 'textarea',
            maxLength: 4000,
            forceRtl: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 6. Modules. Each toggle changes what the REST of the app shows, so the
  //    consequence is spelled out beside every switch, before it is thrown.
  // -------------------------------------------------------------------------
  {
    id: 'modules',
    custom: 'featurePresets',
    sections: [
      { id: 'modules.all', fields: FEATURE_ORDER.map(featureField) },
    ],
  },

  // -------------------------------------------------------------------------
  // 7. Company. Who the tenant is (name, legal details, currency), how it
  //    looks (logo, brand colour) and how it talks (date/time format, the
  //    nouns it uses for its workforce). These used to be three separate
  //    destinations — Company info and Branding each had their own routed
  //    page, and Wording was a tab of its own — for no reason a reader could
  //    tell apart from history. One identity, one tab.
  // -------------------------------------------------------------------------
  {
    id: 'company',
    custom: 'companyIdentity',
    sections: [
      {
        // How a date and a time read. Grouped with the nouns rather than with a
        // "regional" group of its own: both are presentation, neither is
        // policy, and a top-level destination holding one <select> is a
        // signpost to a cupboard.
        id: 'wording.formats',
        fields: [
          {
            id: 'general.dateFormat',
            category: 'general',
            path: 'dateFormat',
            kind: 'select',
            options: DATE_FORMAT_OPTIONS,
          },
          {
            // `timeFormat`'s reader has been wired since the same change that
            // wired `dateFormat`'s: it rides the public branding payload
            // (settings.service.ts getPublicBranding) into `useFormatTime`,
            // which every clock face in the app formats through. Only the
            // control was missing, so the tenant setting could be changed by
            // hand-editing the settings JSON and no other way — while its
            // sibling sat right here with a picker. An asymmetric pair reads as
            // "we format dates your way and times ours".
            id: 'general.timeFormat',
            category: 'general',
            path: 'timeFormat',
            kind: 'select',
            options: TIME_FORMAT_OPTIONS,
          },
        ],
      },
      {
        id: 'wording.profile',
        fields: [
          {
            id: 'terminology.profile',
            category: 'terminology',
            path: 'profile',
            kind: 'select',
            options: TERMINOLOGY_PROFILE_OPTIONS,
          },
        ],
      },
      {
        id: 'wording.overrides',
        fields: TERM_KEYS.map((key) => ({
          id: `terminology.${key}`,
          category: 'terminology' as const,
          path: `overrides.${key}`,
          kind: 'text' as const,
          maxLength: 40,
        })),
      },
    ],
  },

  // -------------------------------------------------------------------------
  // 8. Sign-in and security, plus the shared-tablet PIN lock. Its own module
  //    (`settingsCatalog.security.ts`): this file was long past its budget, and
  //    the security group is the one whose members each carry a paragraph about
  //    what breaks when they are set wrong.
  // -------------------------------------------------------------------------
  SECURITY_GROUP,

  // -------------------------------------------------------------------------
  // 9. The ERP file exchange. Two folders and a day of the month.
  //
  //    It has controls here for the reason this file's own test gives: a
  //    setting whose reader is wired and whose writer is not can only be
  //    changed by hand-editing the settings JSON, and nobody does that on a
  //    live server. Both jobs are off until switched on HERE.
  //
  //    Not marked `pay`. Nothing in this group re-prices an hour — the export
  //    reports hours already computed, and the import decides which payroll a
  //    person's hours are billed TO, not what they are worth.
  // -------------------------------------------------------------------------
  // THE ERP FILE EXCHANGE IS NOT HERE ANY MORE — it is on the ERP screen, in
  // the Connection tab, beside the link it depends on (`FileExchangeCard`).
  //
  // It was a tab here while the connection — where the ERP is, which
  // organisation, which payrolls — was over there, so configuring one
  // integration took two screens and neither said the other existed. The
  // direction of the merge is settled by `erpTabs.ts`: the connection has to
  // stay on that page, because "both other tabs are meaningless until this one
  // is answered". So the seven file settings went to it.
  //
  // The category endpoint is unchanged; only the screen that writes it moved.
  // -------------------------------------------------------------------------
  // -------------------------------------------------------------------------
  // 10. Everything with its own screen. Listed, not duplicated — a second
  //    editor for the same record is how two admins end up disagreeing.
  // -------------------------------------------------------------------------
  {
    id: 'elsewhere',
    custom: 'managedElsewhere',
    sections: [],
  },
];

/** Every field in the catalogue, flat. */
export const ALL_FIELDS: readonly SettingField[] = SETTING_GROUPS.flatMap((g) =>
  g.sections.flatMap((s) => s.fields)
);

/** Field lookup by id — used to resolve `enabledBy` without a scan. */
export const FIELD_BY_ID: ReadonlyMap<string, SettingField> = new Map(
  ALL_FIELDS.map((f) => [f.id, f])
);

/**
 * Screens that own a record type outright. Settings links to them rather than
 * shipping a second editor.
 *
 * `feature` hides a row when its module is off, and it is not optional garnish:
 * every destination below is behind a `ManageRoute` in `App.tsx`, so a row
 * offered while its route is gated is a promise the click cannot keep (§3.11 —
 * a control that cannot act names its obstacle; better still, it is not
 * offered). The flag named here must be the flag the route declares.
 */
export interface ManagedElsewhereLink {
  id: string;
  to: string;
  feature?: FeatureKey;
}

export const MANAGED_ELSEWHERE: readonly ManagedElsewhereLink[] = [
  // Company info, Branding and Devices are NOT here. The first two used to
  // be, each with its own routed page; they are folded into the "Company"
  // tab now (see its header comment), so a second pointer to them here would
  // be the retired route come back as a broken promise. Devices was never a
  // fit for this list at all — it has its own rail row under Approvals
  // ("Device approvals", `navItems.ts`), and this list is for records that
  // have no destination anywhere else in the nav.
  // `workers`, because `App.tsx` guards /worksites with `feature="workers"`.
  // Without it an office-only tenant was offered this row in Settings and met
  // the route guard on the other side of the click.
  { id: 'worksites', to: '/worksites', feature: 'workers' },
  { id: 'departments', to: '/departments', feature: 'orgStructure' },
  { id: 'designations', to: '/designations', feature: 'orgStructure' },
  { id: 'shifts', to: '/shifts', feature: 'shifts' },
  { id: 'leaveTypes', to: '/leave/types', feature: 'leave' },
  { id: 'holidays', to: '/holidays', feature: 'leave' },
];
